/* tws-article-toc.js — the anchors, the active entry, the progress bar.
 *
 * Techwave Store · T4. Loaded with `defer` by sections/tws-article-toc.liquid,
 * which renders the list itself server-side. Three jobs:
 *
 *   1. Put the ids on the article's headings. The slugs are NOT recomputed
 *      here: each one is read off the link that already points at it, so the
 *      two sides cannot drift. Entries are matched to headings BY TEXT, in
 *      order, which steps over the headings that are not the article's (the
 *      theme's own "Tags" and "Comments" blocks, "Popular products"). An
 *      entry that matches nothing is removed from the list rather than left
 *      pointing at an anchor that does not exist.
 *   2. Mark the entry the reader is in (IntersectionObserver, one at a time)
 *      and move the reading-progress bar with the body.
 *   3. Under 992px, move the list under the article title and make it a
 *      collapsible block — open on a tablet, closed on a phone.
 *
 * The sticky offset is measured rather than assumed: Xtra's header is
 * relative at load and becomes fixed on scroll, so its height is read after
 * a scroll has happened as well as at load.
 */
(function () {
  'use strict';

  var nav = document.querySelector('[data-tws-toc]');
  if (!nav) { return; }
  var main = nav.closest('main') || document;
  var body = main.querySelector('[id$="__main-article"]');
  if (!body) { return; }

  var links = Array.prototype.slice.call(nav.querySelectorAll('[data-tws-toc-link]'));
  var headings = Array.prototype.slice.call(body.querySelectorAll('h2, h3'));
  var norm = function (s) { return (s || '').replace(/\s+/g, ' ').trim().toLowerCase(); };

  /* ---- 1. ids -------------------------------------------------------- */
  var targets = [];      /* the headings, in list order */
  var hi = 0;
  links.forEach(function (link) {
    var want = norm(link.textContent);
    var found = null;
    while (hi < headings.length) {
      var h = headings[hi];
      hi += 1;
      if (norm(h.textContent) === want) { found = h; break; }
    }
    if (!found) { link.setAttribute('data-tws-orphan', '1'); return; }
    if (found.id) {
      link.setAttribute('href', '#' + found.id);          /* the author's id wins */
    } else {
      found.id = link.getAttribute('href').slice(1);
    }
    link.__h = found;
    targets.push(link);
  });
  /* An entry with no heading is a link to nowhere. */
  nav.querySelectorAll('[data-tws-orphan]').forEach(function (a) {
    var li = a.closest('li');
    if (li) { li.remove(); }
  });
  if (!targets.length) { nav.remove(); return; }

  /* ---- the sticky offset --------------------------------------------- */
  var headerHeight = function () {
    var h = document.querySelector('header, .header, [id*="__header"]');
    if (!h) { return 72; }
    /* Its HEIGHT is what a sticky rail has to clear, whether it is in the
       flow (at load) or fixed (after a scroll). */
    return Math.max(0, Math.min(h.getBoundingClientRect().height, 160)) || 72;
  };
  var applyOffset = function () {
    var top = headerHeight() + 16;
    document.documentElement.style.setProperty('--tws-sticky-top', top + 'px');
    return top;
  };
  var offset = applyOffset();
  window.addEventListener('resize', function () { offset = applyOffset(); }, { passive: true });

  /* ---- 2. the active entry ------------------------------------------- */
  var setActive = function (link) {
    targets.forEach(function (a) {
      if (a === link) { a.setAttribute('aria-current', 'true'); }
      else { a.removeAttribute('aria-current'); }
    });
    /* One h2 shows its sub-list: the one being read. */
    var li = link.closest('li');
    var top = li.parentElement.classList.contains('tws-toc__sublist')
      ? li.parentElement.closest('li') : li;
    nav.querySelectorAll('.tws-toc__item--open').forEach(function (x) {
      if (x !== top) { x.classList.remove('tws-toc__item--open'); }
    });
    if (top) { top.classList.add('tws-toc__item--open'); }
  };

  if ('IntersectionObserver' in window) {
    var seen = [];
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var i = targets.findIndex(function (a) { return a.__h === e.target; });
        if (i < 0) { return; }
        seen[i] = e.isIntersecting;
      });
      var first = seen.findIndex(Boolean);
      if (first >= 0) { setActive(targets[first]); }
    }, { rootMargin: '-' + offset + 'px 0px -70% 0px', threshold: 0 });
    targets.forEach(function (a) { io.observe(a.__h); });
  }

  /* ---- clicking an entry --------------------------------------------- */
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  nav.addEventListener('click', function (ev) {
    var a = ev.target.closest && ev.target.closest('[data-tws-toc-link]');
    if (!a || !a.__h) { return; }
    ev.preventDefault();
    a.__h.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
    setActive(a);
    if (history.replaceState) { history.replaceState(null, '', a.getAttribute('href')); }
  });

  /* ---- the reading-progress bar --------------------------------------- */
  var bar = nav.querySelector('[data-tws-toc-progress]');
  var ticking = false;
  var progress = function () {
    if (!bar) { return; }
    var r = body.getBoundingClientRect();
    var height = r.height - window.innerHeight;
    var done = height > 0 ? (0 - r.top) / height : 0;
    bar.style.width = Math.max(0, Math.min(1, done)) * 100 + '%';
  };
  window.addEventListener('scroll', function () {
    if (ticking) { return; }
    ticking = true;
    window.requestAnimationFrame(function () { progress(); ticking = false; });
  }, { passive: true });
  progress();

  /* ---- 3. small screens ------------------------------------------------ */
  var small = window.matchMedia('(max-width: 991px)');
  var phone = window.matchMedia('(max-width: 767px)');
  var home = nav.parentElement;
  var wrapper = nav.closest('.tws-article-toc-section');
  var title = nav.querySelector('.tws-toc__title');
  var toggle = null;

  var makeToggle = function () {
    if (toggle) { return toggle; }
    toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = title.className;
    toggle.textContent = title.textContent;
    toggle.setAttribute('aria-expanded', 'true');
    toggle.addEventListener('click', function () {
      var open = nav.getAttribute('data-open') !== 'false';
      nav.setAttribute('data-open', open ? 'false' : 'true');
      toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
    });
    return toggle;
  };

  var place = function () {
    if (small.matches) {
      if (nav.classList.contains('tws-toc--inline')) { return; }
      /* Under the title, which is the last thing in the article's banner
         block; if the theme ever changes that, the list simply stays where
         the template put it. */
      var anchor = body.querySelector('.m6fr, header');
      if (!anchor) { return; }
      anchor.insertAdjacentElement('afterend', nav);
      nav.classList.add('tws-toc--inline');
      nav.setAttribute('data-open', phone.matches ? 'false' : 'true');
      title.replaceWith(makeToggle());
      if (wrapper) { wrapper.classList.add('tws-toc-moved'); }
    } else if (nav.classList.contains('tws-toc--inline')) {
      home.appendChild(nav);
      nav.classList.remove('tws-toc--inline');
      nav.removeAttribute('data-open');
      if (toggle && toggle.parentElement) { toggle.replaceWith(title); }
      if (wrapper) { wrapper.classList.remove('tws-toc-moved'); }
    }
  };
  place();
  if (small.addEventListener) { small.addEventListener('change', place); }
}());
